/**
 * Privacy + Terms — minimal generic policies for the sales bundle so the
 * footer links resolve to real pages instead of 404. Update copy any time
 * by editing the constants below; no rebuild required to swap legal name
 * or contact email if you Find-and-Replace the compiled JS.
 */
import { Link } from "react-router-dom";
import HustlePlateLogo from "./HustlePlateLogo";
import "./LegalPage.css";

const CONTACT_EMAIL = "hello@hustleplate.au";
const ENTITY = "HustlePlate Pty Ltd";
const JURISDICTION = "Queensland, Australia";
const EFFECTIVE = "27 February 2026";

const SHELL = ({ title, eyebrow, children }) => (
  <div className="lp-root">
    <header className="lp-nav">
      <div className="lp-container lp-nav__inner">
        <Link to="/" className="lp-nav__logo" aria-label="HustlePlate home">
          <HustlePlateLogo size={32} />
        </Link>
        <Link to="/" className="lp-nav__back">← Back to home</Link>
      </div>
    </header>

    <main className="lp-main">
      <div className="lp-container">
        <p className="lp-eyebrow">{eyebrow}</p>
        <h1 className="lp-title">{title}</h1>
        <p className="lp-effective">Effective {EFFECTIVE}</p>
        <div className="lp-body">{children}</div>
      </div>
    </main>

    <footer className="lp-foot">
      <div className="lp-container lp-foot__inner">
        <Link to="/" aria-label="HustlePlate home">
          <HustlePlateLogo size={28} />
        </Link>
        <div className="lp-foot__links">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </div>
        <p className="lp-foot__cr">© {new Date().getFullYear()} {ENTITY}.</p>
      </div>
    </footer>
  </div>
);

export function PrivacyPage() {
  return (
    <SHELL eyebrow="Legal" title="Privacy Policy">
      <p>
        This Privacy Policy explains how {ENTITY} (<em>"HustlePlate"</em>, <em>"we"</em>,
        <em> "us"</em>) collects, uses, stores and discloses your personal information
        when you visit our website or use our restaurant management platform
        (the <strong>"Service"</strong>). We comply with the Australian Privacy
        Principles (APPs) under the <em>Privacy Act 1988 (Cth)</em>.
      </p>

      <h2>1. Information we collect</h2>
      <ul>
        <li><strong>Account information</strong> — name, email, phone, business name, role.</li>
        <li><strong>Usage information</strong> — pages visited, features used, IP address, user-agent, approximate location derived from IP.</li>
        <li><strong>Order &amp; reservation data</strong> — when diners place orders or bookings through a restaurant using HustlePlate.</li>
        <li><strong>Payment data</strong> — processed by Stripe; we never store card numbers.</li>
        <li><strong>Marketing communications</strong> — lead-form submissions sent to us via the website.</li>
      </ul>

      <h2>2. How we use your information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Operate, secure and improve the Service.</li>
        <li>Respond to enquiries, demo requests and support tickets.</li>
        <li>Send transactional emails (receipts, booking confirmations, password resets).</li>
        <li>Send marketing emails — only with your consent, with one-click unsubscribe.</li>
        <li>Detect and prevent fraud, abuse and platform misuse.</li>
        <li>Comply with our legal obligations.</li>
      </ul>

      <h2>3. How we share information</h2>
      <p>
        We do <strong>not</strong> sell your personal information. We share it only with:
      </p>
      <ul>
        <li>Service providers acting on our behalf (hosting, email delivery, payment processing, analytics) under written confidentiality terms.</li>
        <li>The restaurant whose page you interacted with — for example, your booking is shared with that venue.</li>
        <li>Law enforcement or regulators when required by law.</li>
      </ul>

      <h2>4. Data security &amp; storage</h2>
      <p>
        Data is stored on encrypted servers in Australia and the European Union.
        We use TLS in transit, AES-128 at rest for sensitive integration
        credentials, and apply least-privilege access controls.
      </p>

      <h2>5. Your rights</h2>
      <p>
        You may request access to, correction of, or deletion of your personal
        information at any time by emailing <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        We respond within 30 days. You may also complain to the
        Office of the Australian Information Commissioner (<a href="https://www.oaic.gov.au" target="_blank" rel="noreferrer">oaic.gov.au</a>) if you are unsatisfied with our response.
      </p>

      <h2>6. Cookies</h2>
      <p>
        We use cookies to keep you signed in, remember your preferences (language, cart) and
        measure anonymous usage. You can clear cookies in your browser at any time —
        some Service features will require you to sign in again.
      </p>

      <h2>7. Children</h2>
      <p>
        The Service is not intended for children under 16. We do not knowingly
        collect personal information from children.
      </p>

      <h2>8. Changes to this policy</h2>
      <p>
        We may update this policy from time to time. Material changes will be
        announced on this page and, where appropriate, by email.
      </p>

      <h2>9. Contact us</h2>
      <p>
        <strong>{ENTITY}</strong><br />
        Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a><br />
        Jurisdiction: {JURISDICTION}
      </p>

      <p className="lp-disclaimer">
        <em>This is a generic policy template and not legal advice. Replace
        with your own counsel-reviewed policy before launch.</em>
      </p>
    </SHELL>
  );
}

export function TermsPage() {
  return (
    <SHELL eyebrow="Legal" title="Terms of Service">
      <p>
        These Terms of Service (<strong>"Terms"</strong>) govern your access to and
        use of HustlePlate. By signing up for, accessing or using the Service,
        you agree to be bound by these Terms.
      </p>

      <h2>1. Definitions</h2>
      <ul>
        <li><strong>"Service"</strong> — the HustlePlate platform, including all features, APIs, mobile apps and template galleries.</li>
        <li><strong>"You"</strong> — the restaurant owner, manager or staff member using the Service.</li>
        <li><strong>"Diner"</strong> — the end customer interacting with a restaurant's HustlePlate-powered website.</li>
      </ul>

      <h2>2. Eligibility</h2>
      <p>You must be at least 18 years old and authorised to bind the business you are registering on the Service.</p>

      <h2>3. Your account</h2>
      <p>
        Keep your password confidential. You are responsible for all activity
        on your account. Tell us immediately at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> if you suspect unauthorised access.
      </p>

      <h2>4. Subscription &amp; billing</h2>
      <ul>
        <li>The free Starter plan is genuinely free. No credit card required.</li>
        <li>Paid plans are billed monthly in advance via Stripe.</li>
        <li>You can cancel at any time from the Admin dashboard — no cancellation fee, no lock-in.</li>
        <li>We do not offer pro-rata refunds for the current billing period; you keep access until period end.</li>
      </ul>

      <h2>5. Acceptable use</h2>
      <p>You agree NOT to:</p>
      <ul>
        <li>Use the Service for any unlawful purpose, or to publish menus that misrepresent allergens or pricing.</li>
        <li>Reverse-engineer, scrape, or otherwise abuse the platform.</li>
        <li>Send unsolicited bulk communications through our messaging tools.</li>
      </ul>

      <h2>6. Content ownership</h2>
      <p>
        You retain ownership of menus, photos and brand assets you upload.
        By uploading, you grant us a worldwide, royalty-free licence to display,
        cache and reproduce that content solely as needed to operate the Service.
      </p>

      <h2>7. Diner data</h2>
      <p>
        When diners place orders or bookings, you are the data controller of
        that information for the purposes of the Privacy Act. HustlePlate
        acts as your processor.
      </p>

      <h2>8. Service availability</h2>
      <p>
        We target 99.9% monthly uptime but do not guarantee uninterrupted access.
        Maintenance windows are scheduled outside Australian dinner-service hours
        where possible.
      </p>

      <h2>9. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, HustlePlate's liability is
        limited to fees paid by you in the three months preceding the claim.
        Nothing in these Terms limits non-excludable consumer guarantees under
        the Australian Consumer Law.
      </p>

      <h2>10. Termination</h2>
      <p>
        Either party may terminate this agreement at any time. We may suspend
        your account immediately for breach of these Terms, payment failure or
        suspected fraud.
      </p>

      <h2>11. Governing law</h2>
      <p>These Terms are governed by the laws of {JURISDICTION}.</p>

      <h2>12. Contact</h2>
      <p>
        <strong>{ENTITY}</strong><br />
        Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
      </p>

      <h2>13. Third-party services</h2>
      <p>
        The Service uses the following third-party providers under their own
        terms — your use of the Service constitutes acceptance of their
        applicable terms where you interact with them:
      </p>
      <ul>
        <li>
          <strong>Google Translate</strong> — multilingual translation widget on our
          marketing site and tenant pages. Translations are provided "as is" by
          Google and may contain inaccuracies. See
          {" "}<a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Google's Terms of Service</a>
          {" "}and
          {" "}<a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
        </li>
        <li>
          <strong>Stripe</strong> — payment processing. See <a href="https://stripe.com/au/legal/consumer" target="_blank" rel="noreferrer">Stripe's terms</a>.
        </li>
        <li>
          <strong>Cloudflare</strong> — content delivery and security for media assets served via cdn.hustleplate.au.
        </li>
      </ul>

      <p className="lp-disclaimer">
        <em>This is a generic terms template and not legal advice. Replace
        with your own counsel-reviewed terms before launch.</em>
      </p>
    </SHELL>
  );
}
